/**
 * Peak Checkout — storefront script.
 *
 * Bundled by esbuild (`npm run build:storefront`) into `public/peak-checkout.js` as a minified
 * ES2017 IIFE and served from this service's origin. It executes as first-party code on the
 * merchant's shop, so it imports nothing from `@/` and adds nothing to `window` except
 * `window.peakCheckout`.
 *
 * What it does, in order:
 *
 *  1. Reads `data-peak-key` off its own `<script>` tag and derives the rest — the service
 *     origin from the tag's own `src`, the shop domain and the cart URL from `window.Shopify` —
 *     so the whole install is one line in `layout/theme.liquid` (`./bootstrap.ts`,
 *     `docs/STOREFRONT.md` §1). Every derived value keeps an attribute override, which is what
 *     `theme/snippets/peak-checkout.liquid` renders on the advanced path.
 *  2. Hides the express buttons synchronously at start unless the session's last config answer
 *     said `enabled: false` (D259, D385). They stay hidden unless the config answers
 *     `enabled: false`: an unreadable config keeps them hidden, and a `hideExpressButtons: false`
 *     answer is ignored, because a visible Shop Pay button is a route to Shopify.
 *  3. Fetches `GET {service}/api/storefront/config?key=…` once per page load (30 s
 *     `sessionStorage` cache; one retry, D255). The governing rule (D385, `./outcome.ts`):
 *     Shopify's checkout is never a fallback. An `enabled: false` answer — a person switched the
 *     store off, or the key is unknown — leaves Shopify's native checkout to the shopper without a
 *     request. That is the kill switch; it needs no theme edit, and it is the one case the script
 *     steps aside for. A config that cannot be read at all (a down service, a blocked request, a
 *     5xx) is not that (D375): a checkout still goes to the create endpoint, which applies the kill
 *     switch itself (`409 disabled` → native). A checkout control used **while the config is still
 *     loading** is held (busy state) until it answers (D255).
 *  4. Delegates `click` (capture) and `submit` (capture) on `window` and `document` (D259: once
 *     per event), attached at init, so cart drawers that
 *     are rendered later are covered without a `MutationObserver` polling loop. The click's
 *     real target is read from `composedPath()`, so a drawer rendered in an open shadow root
 *     (UpCart) is covered too; where the browser has the Navigation API, a scripted same-origin
 *     navigation to `/checkout`, `/cart/checkout` (D372), `/cart/c/{token}` or `/checkouts/…` is
 *     taken over as well (D212, `./cart-apps.ts`; D259, D385), the pages after payment excepted.
 *  5. On intercept: busy state, `GET /cart.js` (or, for a cart-form post, with or without a
 *     checkout button behind it, `POST {action}/update.js` with the form's fields, D255, D259;
 *     for a `/cart/checkout` link, `POST {cart}/update.js` with its query's `note`,
 *     `attributes[…]` and `discount`, D372),
 *     `POST {service}/api/checkout/create` — the
 *     cart plus the shop's own GA4 and Google Ads click-id cookies, when the tag set them
 *     (`./ga-cookies.ts`, `./google-click-cookies.ts`), and the click ids Peak itself kept from
 *     the landing URL when the tag did not (`./url-click-ids.ts`, captured in step 1) — then
 *     `window.location.assign(redirectUrl)`. The shopper's Shopify market rides along when the
 *     theme rendered it onto the tag (`./market.ts`, D210), and the A/B-experiment unit id and
 *     QA forced arm when there are any (`./experiment.ts`, D239), and — on a store with TrackBee on
 *     — TrackBee's visitor id from the shop's `localStorage` with the shopper's Customer Privacy
 *     answer (`./trackbee.ts`, D367). Any other outcome (`./outcome.ts`, D385): an empty cart goes
 *     to the cart page; a store that turns out to be switched off to Shopify's checkout, or to
 *     the intercepted link itself when it was a checkout link (`/cart/c/{token}?key=…`, D204);
 *     every failure keeps the shopper on the store, after one more automatic attempt when that is
 *     worth it, with a Peak notice and a retry (`./notice.ts`). A token link whose token is not
 *     the session cart's no longer goes to the link: the session cart goes to Peak.
 *  6. Every handoff that does not end at Peak — and a checkout click while the script is not active
 *     (switched off) — is reported to `POST {service}/api/storefront/fallback` with a
 *     fire-and-forget beacon, so the reason reaches our logs (D247, `./fallback-report.ts`).
 *     Diagnostics only: where the shopper goes does not change.
 *  7. A checkout link opened in a new tab or window — middle click, ⌘/Ctrl/Shift-click, the
 *     context menu, a drag — opens the current page with a `#peak-checkout` marker instead, and
 *     the script on that page runs the same handoff on load; ⌘/Ctrl/Shift on a checkout button
 *     opens that marker page with `window.open` (D254, `./new-tab.ts`).
 *  8. A checkout tap made before this script ran — held by an inline early-tap stub a theme still
 *     carries in front of our tag (D343; Peak stopped printing it with D360, the themes that
 *     pasted it keep it until they re-paste) — is taken over at init and replayed through the
 *     listeners above, so it gets exactly the handoff a live tap would; its create request and any
 *     report carry `early: true` (`replayEarlyTap`). On a theme without the stub, such a tap goes
 *     to Shopify's checkout, as it did before D343: nothing of ours is running yet.
 *  9. A script that starts more than 6 s after the navigation sends one `script_ready_late` load
 *     report with its delay, so a page view on which Peak was not there in time is on record
 *     (D344, provisional, `./load-report.ts`); a normal page view sends nothing.
 * 10. A same-origin click on a Shopify cart permalink (`/cart/{variant}:{qty},…`) goes to the
 *     Peak buy link for exactly those items, `{serviceUrl}/b/{key}/{items}{search}` (D260,
 *     restored by D359, `./buy-link.ts`, `takePermalink`), an unreadable config included (D385).
 *     A permalink opened from outside the store (an ad) never reaches this script: Shopify
 *     redirects it to checkout server-side.
 *
 * Without a usable tag (`not_configured`) nothing here can act: no key, no service to send anyone
 * to. Known limitation, by construction: a customer who types `/checkout` into the address bar
 * cannot be intercepted client-side — no script of ours is running yet. `docs/STOREFRONT.md`
 * records it, and Phase 6 may add an opt-in theme-level redirect.
 */

import {
  describeBootstrap,
  hasKeyAttribute,
  resolveBootstrap,
  PEAK_SCRIPT_SELECTOR,
  type PeakBootstrap,
} from './bootstrap';
import { buildCreatePayload, createEndpointUrl, isUsableCart, PEAK_DEFAULT_CART_URL } from './cart';
import { cartUpdateFields, cartUpdateUrlOf, linkUpdateFields } from './cart-form';
import {
  clearCachedConfig,
  configEndpointUrl,
  parseConfigResponse,
  readCachedConfig,
  writeCachedConfig,
  type YcStorageLike,
  type YcStorefrontConfig,
} from './config';
import {
  captureForcedArm,
  initVisitorId,
  readExperimentHint,
  stripForcedArm,
  type PeakExperimentEnv,
} from './experiment';
import { applyExpressPolicy, type YcDocumentLike } from './express';
import { readAnalyticsCookies } from './ga-cookies';
import { readMarketContext, withCartUrl } from './market';
import {
  controlKindOf,
  createFallbackReporter,
  customerStateOf,
  fallbackEndpointUrl,
  sendFallbackReport,
  type PeakConfigState,
  type PeakControlKind,
  type PeakFallbackReporter,
} from './fallback-report';
import { cartTokenFromCookie, loadDelayMs, readyLateReport } from './load-report';
import { closeNotice, showNotice } from './notice';
import { decideOutcome, isRepeatable, PEAK_NATIVE_CHECKOUT_PATH, type YcOutcome } from './outcome';
import {
  cartCheckoutLinkOf,
  checkoutLinkKind,
  isCheckoutHref,
  isSameCartToken,
  linkTokenOf,
  PEAK_OPT_OUT_ATTRIBUTE,
  resolveCheckoutControl,
  type YcElementLike,
} from './selectors';
import {
  closestComposed,
  originalTarget,
  upcartShadowDocument,
  UPCART_RENDER_EVENTS,
} from './cart-apps';
import { captureUrlClickIds, landingSearch } from './url-click-ids';
import { newTabMarkerUrl, readNewTabMarker, wantsNewTab } from './new-tab';
import { buyLinkHref, permalinkOf, type PeakPermalink } from './buy-link';
import { readTrackBeeHint, readTrackBeeVisitor, withTrackBeeVisitor } from './trackbee';

/** Bumped by hand; surfaced as `window.peakCheckout.version` for support. */
const PEAK_CHECKOUT_VERSION = '1.8.0';

const LOG_PREFIX = '[peak-checkout]';

/**
 * Ceilings so a hanging request can never leave the customer staring at a busy button: per
 * attempt, and for the request with its one retry (`fetchJson`, D255). Each request is retried
 * once after `RETRY_DELAY_MS`, and only when at least `MIN_RETRY_MS` of its total is left.
 *
 * - Config: 4 s per attempt, 8 s in all. A held click waits for it, so it stays short.
 * - Cart (`cart.js`, `update.js`): 10 s per attempt, 16 s in all for the whole read, its recoveries
 *   included (`loadCart`, D385). Shopify answered `cart.js` in 1.4–5 s on 2026-10-15 with page
 *   TTFBs up to 9.8 s, against the old 6 s ceiling.
 * - Create: 10 s per attempt (as before), 21 s in all (D375; 15 s before), so a second attempt
 *   after a timeout still gets its full 10 s. Retried once on a network error, a timeout, a 500,
 *   502, 503 or 504 (D375 added the timeout and the 500), and on a 429 whose `Retry-After` is 5 s
 *   or less, after that wait (D375); never on another 4xx. A repeat from the same browser is
 *   handed the checkout the first attempt created once that one is stored
 *   (`src/lib/checkout/create/reuse.ts`); a first attempt still running on the server (a timeout)
 *   may leave a second, unused checkout row behind, which expires.
 */
const CONFIG_ATTEMPT_MS = 4_000;
const CONFIG_TOTAL_MS = 8_000;
const CART_ATTEMPT_MS = 10_000;
const CART_TOTAL_MS = 16_000;
const CREATE_ATTEMPT_MS = 10_000;
const CREATE_TOTAL_MS = 21_000;
const RETRY_DELAY_MS = 500;
const MIN_RETRY_MS = 1_000;

/**
 * One more attempt of the whole handoff before the notice (D385, `isRepeatable`), after this pause,
 * and only when the first one failed within `REPEAT_WITHIN_MS`: a blip gets a second chance, while
 * a request that used up its ceilings goes to the notice rather than keep a button busy for a minute.
 */
const REPEAT_DELAY_MS = 1_000;
const REPEAT_WITHIN_MS = 8_000;

/** State attribute set on the control while the handoff is in flight. */
const STATE_ATTRIBUTE = 'data-peak-state';

type PeakCheckoutApi = {
  version: string;
  /** Re-read the config, ignoring the session cache. For support and QA. */
  reinit: () => void;
};

/**
 * What an installed early-tap stub's `take()` hands over (D343): the held tap, its listeners and
 * deadline already released. The stub's source was deleted by D360; this is the contract every
 * shipped stub (`stub-1` to `stub-3`) keeps, so the themes that still carry one keep working.
 */
type PeakEarlyTap = {
  /** `click` (a checkout control) or `submit` (a form post, with or without a checkout submitter). */
  type: 'click' | 'submit';
  /** The checkout control: the clicked one, or the submit's submitter when it is one; else `null`. */
  control: HTMLElement | null;
  /** The submitted form, for `submit`; `null` for `click`. */
  form: HTMLFormElement | null;
  /** The submit's submitter, any kind, for `submit`; `null` otherwise. */
  submitter: HTMLElement | null;
};

/** The hand-over hook an installed stub puts on `window`; called once, at init (D343). */
type PeakEarlyTapHook = { take: () => PeakEarlyTap | null };

type YcWindow = Window &
  typeof globalThis & {
    __peakCheckoutLoaded?: boolean;
    /** An installed early-tap stub's hand-over hook (D343; no longer printed since D360). */
    __peakCheckoutEarly?: PeakEarlyTapHook;
    peakCheckout?: PeakCheckoutApi;
    Shopify?: unknown;
  };

/** Key, service, shop and cart URL — `./bootstrap.ts` owns where each of them comes from. */
type Bootstrap = PeakBootstrap;

/** `initVisitorId` ran on this page (D244: once, after a config that says `experiments`). */
let visitorIdStarted = false;

/** Resolved config for this page load; `null` until the fetch answers. */
let activeConfig: YcStorefrontConfig | null = null;

/**
 * Where the config stands, for the fallback report (D247). `enabled` exactly when
 * `activeConfig` is set; `configReason` says why it is `unavailable`.
 */
let configState: PeakConfigState = 'loading';
let configReason: string | undefined;

/** The session's last config answer, any age, read once at init (`readCachedConfig`). */
let hint: YcStorefrontConfig | null = null;

/** The config resolution a held checkout waits for (D255); replaced when it is asked again. */
let configReady: Promise<void> = Promise.resolve();

/** A failed config was asked again for a held checkout, which happens once per page (D255). */
let askedAgain = false;

/** The express-button style is applied right now (`applyExpressEverywhere`). */
let expressHidden = false;

/** The fallback reporter (`./fallback-report.ts`); `null` until init has a bootstrap. */
let reporter: PeakFallbackReporter | null = null;

/** So the pre-rename-snippet warning is logged once per page load, not once per `reinit`. */
let legacyAttributesWarned = false;

/** Same for the one `console.debug` line naming where each derived value came from. */
let sourcesLogged = false;

/** One handoff at a time — the double-click guard. */
let inFlight = false;

/**
 * Set while a tap an installed early-tap stub held is replayed, and through the handoff it starts
 * (D343, `replayEarlyTap`): the create request and every report meanwhile carry
 * `early: true`, so the logs can count the taps the stub caught.
 */
let earlyTap = false;

/**
 * Set while one of our own navigations to Shopify's checkout starts (`goNative`), so the
 * Navigation API safety net lets it through: without it a store switched off mid-handoff would
 * intercept its own way out, forever.
 */
let nativeNavigation = false;

/**
 * Set during the dispatch of a click inside `data-peak-ignore` (across shadow roots), so the
 * safety net also leaves alone the navigation an app's click handler starts from it.
 */
let ignoredClick = false;

/**
 * The checkout anchor whose `href` is pointed at the new-tab marker page right now (D254), with
 * the `href` it had and the marker it was given; `null` when none is. `restoreHref` puts the
 * original back — and only when the anchor still holds our marker, so an app that re-rendered
 * the link meanwhile keeps its new `href`.
 */
let swapped: { anchor: HTMLAnchorElement; href: string; marker: string } | null = null;

function warn(reason: string, detail?: unknown): void {
  // One line, with a machine-readable reason a merchant can quote in a support ticket. The only
  // other thing this script ever prints is the single `console.debug` line from
  // `logBootstrapSources` below.
  if (detail === undefined) console.warn(LOG_PREFIX + ' ' + reason);
  else console.warn(LOG_PREFIX + ' ' + reason, detail);
}

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

/**
 * Our own `<script>` element, captured **at module evaluation**.
 *
 * `document.currentScript` is set while a classic script executes — `defer` included, which is
 * how the one-line install loads us — but it is `null` inside anything asynchronous. Reading it
 * here, at the top level, is therefore the only reliable moment: `init()` runs at the end of
 * this module and `reinit()` runs much later, and both need the same element.
 *
 * `typeof document` is guarded so importing this module outside a browser (a unit test, a
 * server-side bundle that should never happen) cannot throw at import time.
 */
const CURRENT_SCRIPT: Element | null =
  typeof document === 'undefined' ? null : ((document.currentScript as Element | null) ?? null);

/**
 * Our own `<script>` element.
 *
 * The captured `currentScript` first; the attribute lookup is the fallback for a theme that
 * injects the tag differently (a tag-manager copy, an app block) and for `reinit()` on a page
 * where the element was replaced.
 */
function findScript(): Element | null {
  if (CURRENT_SCRIPT !== null && hasKeyAttribute(CURRENT_SCRIPT)) return CURRENT_SCRIPT;
  return document.querySelector(PEAK_SCRIPT_SELECTOR);
}

/**
 * The tag's `src`, absolute.
 *
 * `HTMLScriptElement.src` is the resolved URL, which is what `originOfScriptSrc` wants. The
 * `getAttribute` branch covers an element that is not an `HTMLScriptElement` at all (a theme
 * that cloned the tag into some custom element) and resolves it against the document.
 */
function scriptSrcOf(script: Element): string | null {
  try {
    if (script instanceof HTMLScriptElement && script.src !== '') return script.src;
    const raw = script.getAttribute('src');
    if (raw === null || raw.trim() === '') return null;
    return new URL(raw, document.baseURI).href;
  } catch {
    return null;
  }
}

/** `window.Shopify`, or `undefined` off the Online Store (the demo page, a headless front end). */
function shopifyGlobal(): unknown {
  try {
    return (window as YcWindow).Shopify;
  } catch {
    // A sandboxed or proxied `window` may refuse the property; that is a missing global.
    return undefined;
  }
}

/**
 * The one `console.debug` line: which source answered for each derived value.
 *
 * Support reads it off a screenshot of the console instead of asking for a theme export —
 * `service=… (script-src) shop=… (shopify-global) cart=… (default)`. `debug` rather than `log`
 * so it sits under the browser's verbose level and never looks like an error; once per page
 * load, not once per `reinit`.
 */
function logBootstrapSources(bootstrap: Bootstrap): void {
  if (sourcesLogged) return;
  sourcesLogged = true;
  console.debug(LOG_PREFIX + ' ' + PEAK_CHECKOUT_VERSION + ' ' + describeBootstrap(bootstrap));
}

function readBootstrap(): Bootstrap | null {
  const script = findScript();
  if (script === null) return null;

  const bootstrap = resolveBootstrap(script, {
    scriptSrc: scriptSrcOf(script),
    shopifyGlobal: shopifyGlobal(),
    hostname: window.location.hostname,
  });
  if (bootstrap === null) return null;

  logBootstrapSources(bootstrap);

  // The old snippet still works, through the `/yc-checkout.js` rewrite and the `data-yc-*`
  // fallback, but it is one deploy away from being the only thing holding it up — so say so
  // once, where the merchant or whoever installed the theme will see it.
  if (bootstrap.legacyAttributes && !legacyAttributesWarned) {
    legacyAttributesWarned = true;
    warn(
      'theme.snippet_outdated',
      'the script tag uses the pre-rename data-yc-* attributes; paste the current peak-checkout snippet into the theme (docs/STOREFRONT.md)',
    );
  }

  return bootstrap;
}

/** `sessionStorage`, or a no-op when the browser refuses it (Safari private mode). */
/** `window.localStorage`, or `null` when the browser refuses it (Safari private mode, a policy). */
function local(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function storage(): YcStorageLike {
  try {
    const candidate = window.sessionStorage;
    if (candidate !== null && candidate !== undefined) return candidate;
  } catch {
    // Fall through to the no-op below.
  }
  return {
    getItem: () => null,
    setItem: () => undefined,
    removeItem: () => undefined,
  };
}

/** The page slice `./experiment.ts` reads. A page without `crypto` makes `fill` throw: no id. */
function experimentEnv(): PeakExperimentEnv {
  return {
    document,
    shopifyGlobal: shopifyGlobal(),
    secure: window.location.protocol === 'https:',
    fill: (bytes) => crypto.getRandomValues(bytes),
  };
}

function doc(): YcDocumentLike {
  return document as unknown as YcDocumentLike;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

/**
 * A response: its status, its JSON body (`undefined` when the body is not JSON) and, on a 429,
 * its `Retry-After` (D375; the create endpoint exposes it to the page, `src/lib/http/cors.ts`).
 */
type Answer = { status: number; body: unknown; after?: string | null };

const TIMED_OUT = new Error('timeout');

/**
 * The pause before the one retry, in milliseconds, or `-1` for none. A read: a network error
 * (`0`), a timeout (`-1`), a 429 or a 5xx, after `RETRY_DELAY_MS` (D255); a 429 with a `Retry-After`
 * of 5 s or less after that wait (D385).
 */
const retryRead = (status: number, after?: string | null): number =>
  status < 1 || status === 429 || status > 499
    ? (status === 429 && after && +after <= 5 && +after * 1000) || RETRY_DELAY_MS
    : -1;

/**
 * The create call (D255, D375): a network error, a timeout, a 500, 502, 503 or 504 after
 * `RETRY_DELAY_MS`; a 429 after its `Retry-After` in seconds (`0`: `RETRY_DELAY_MS`) when that is
 * at most 5 s — no header, an HTTP date, a negative or a longer wait: no retry; never another 4xx.
 */
const retryCreate = (status: number, after?: string | null): number =>
  status === 429
    ? after && +after <= 5
      ? +after * 1000 || RETRY_DELAY_MS
      : -1
    : status < 1 || status === 500 || (status > 501 && status < 505)
      ? RETRY_DELAY_MS
      : -1;

/**
 * `fetch` plus its JSON body under one hard timeout per attempt — the body read included, so a
 * stalled body cannot outlast it, and raced against a timer so a `fetch` that ignores the abort
 * cannot either — with one retry when `retry` answers a pause for the status (`0` network error,
 * `-1` timeout) and that pause plus `MIN_RETRY_MS` still fits inside `totalMs` (D255, D375). The
 * last error is rethrown; any HTTP answer is returned.
 * ES2017-safe, so no `AbortSignal.timeout`.
 */
async function fetchJson(
  input: string,
  init: RequestInit,
  attemptMs: number,
  totalMs: number,
  retry: (status: number, after?: string | null) => number,
): Promise<Answer> {
  const deadline = Date.now() + totalMs;
  for (let attempt = 0; ; attempt += 1) {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let answer: Answer | undefined;
    let failure: unknown;
    let status: number;
    try {
      answer = await Promise.race([
        fetch(input, { ...init, signal: controller.signal }).then(async (response) => {
          let body: unknown;
          try {
            body = await response.json();
          } catch {
            // Not JSON: `body` stays `undefined`, and the caller decides what that means.
          }
          return {
            status: response.status,
            body,
            after: response.status === 429 ? response.headers.get('Retry-After') : null,
          };
        }),
        new Promise<never>((_, reject) => {
          timer = setTimeout(
            () => {
              controller.abort();
              reject(TIMED_OUT);
            },
            Math.min(attemptMs, deadline - Date.now()),
          );
        }),
      ]);
      status = answer.status;
    } catch (error) {
      failure = error;
      status = error === TIMED_OUT ? -1 : 0;
    } finally {
      clearTimeout(timer);
    }
    const wait = attempt === 0 ? retry(status, answer && answer.after) : -1;
    if (wait >= 0 && deadline - Date.now() >= wait + MIN_RETRY_MS) {
      await new Promise((resolve) => setTimeout(resolve, wait));
      continue;
    }
    if (answer !== undefined) return answer;
    throw failure;
  }
}

async function fetchConfig(bootstrap: Bootstrap): Promise<YcStorefrontConfig | null> {
  try {
    const answer = await fetchJson(
      configEndpointUrl(bootstrap.serviceUrl, bootstrap.publicKey),
      { method: 'GET', mode: 'cors', credentials: 'omit', headers: { Accept: 'application/json' } },
      CONFIG_ATTEMPT_MS,
      CONFIG_TOTAL_MS,
      retryRead,
    );
    if (answer.status < 200 || answer.status > 299) {
      configReason = 'config_http_' + String(answer.status);
      warn(configReason);
      return null;
    }
    const parsed = parseConfigResponse(answer.body, bootstrap.serviceUrl);
    if (parsed === null) configReason = 'config_invalid';
    return parsed;
  } catch (error) {
    // Network failure, CORS refusal, timeout, offline: an unreadable config (`resolveConfig`).
    configReason = 'config_unavailable';
    warn(configReason, error);
    return null;
  }
}

/**
 * Resolve the config for this page load and apply everything that depends on it.
 *
 * `useCache: false` is `reinit()` — it forces a fresh fetch so a merchant can verify a kill
 * switch without waiting out the TTL.
 */
async function resolveConfig(bootstrap: Bootstrap, useCache: boolean): Promise<void> {
  const store = storage();
  const now = Date.now();

  let config = useCache ? readCachedConfig(store, bootstrap.publicKey, now) : null;
  configReason = undefined;
  if (config === null) {
    if (!useCache) clearCachedConfig(store, bootstrap.publicKey);
    config = await fetchConfig(bootstrap);
    if (config !== null) writeCachedConfig(store, bootstrap.publicKey, config, Date.now());
  }

  if (config === null || !config.enabled) {
    // `enabled: false` — unknown key, paused tenant, kill switch, uninstalled app — is the one
    // answer the listeners stand down for (`active`), and the only one that shows the express
    // buttons again. An unreachable service changes neither (D385).
    activeConfig = null;
    configState = config === null ? 'unavailable' : 'disabled';
    applyExpressEverywhere(config === null);
    restoreHref();
    return;
  }

  activeConfig = config;
  configState = 'enabled';
  configReason = undefined;
  // `config.hideExpressButtons` is ignored since D385: a visible Shop Pay button is a route to
  // Shopify. Parsed still, so an older answer does not break (`parseConfigResponse`).
  applyExpressEverywhere(true);
  // The A/B-experiment unit id (`_peak_vid`, gated on the Customer Privacy API's analytics
  // purpose, `./experiment.ts`, D239) — only on a store that runs an experiment (D244), and once
  // per page: `reinit()` re-resolves the config, and the consent listener must not stack.
  if (config.experiments && !visitorIdStarted) {
    visitorIdStarted = true;
    initVisitorId(experimentEnv());
  }
}

/**
 * Do the listeners act? Always, except when the config answered `enabled: false`, a person's
 * decision (D385). While it loads a checkout is held for it (`configured`, D255); when it cannot
 * be read the checkout still goes to the create endpoint with the bootstrap's service, which
 * applies the kill switch and every other availability rule itself (`start`, D375).
 */
function active(): boolean {
  return configState !== 'disabled';
}

/**
 * The config a held checkout goes on with, or `null` when it is not `enabled` (D255). A config
 * that failed is asked again here, once per page, for a session whose last answer was enabled.
 * Bounded by `fetchConfig`'s total.
 */
function configured(bootstrap: Bootstrap): Promise<YcStorefrontConfig | null> {
  if (configState === 'unavailable' && !askedAgain && hint !== null && hint.enabled) {
    askedAgain = true;
    configState = 'loading';
    configReady = resolveConfig(bootstrap, true);
  }
  const current = (): YcStorefrontConfig | null => activeConfig;
  return configReady.then(current, current);
}

/**
 * The express policy for the page and for UpCart's shadow root, which a stylesheet in `<head>`
 * cannot reach. Called with the resolved config and again whenever UpCart (re)renders.
 */
function applyExpressEverywhere(hide: boolean): void {
  expressHidden = hide;
  applyExpressPolicy(doc(), hide);
  const shadow = upcartShadowDocument(document);
  if (shadow !== null) applyExpressPolicy(shadow, hide);
}

// ---------------------------------------------------------------------------
// Busy state
// ---------------------------------------------------------------------------

function setBusy(control: Element, busy: boolean): void {
  try {
    if (busy) {
      control.setAttribute('aria-busy', 'true');
      control.setAttribute(STATE_ATTRIBUTE, 'loading');
    } else {
      control.removeAttribute('aria-busy');
      control.removeAttribute(STATE_ATTRIBUTE);
    }
    if (control instanceof HTMLButtonElement || control instanceof HTMLInputElement) {
      control.disabled = busy;
    }
  } catch {
    // A busy indicator is never worth breaking the handoff for.
  }
}

// ---------------------------------------------------------------------------
// The handoff
// ---------------------------------------------------------------------------

/**
 * A Shopify cart-form post taken over without a checkout button behind it (D255, `takeForm`):
 * the form, which a store that turns out to be switched off submits to Shopify as it was; its
 * `update.js` URL; and the fields that must reach the cart first (`cartUpdateFields`), `null` when
 * there are none. `form` is `null` for a `/cart/checkout` link's query (D372, `linkPost`).
 */
type CartFormPost = { form: HTMLFormElement | null; updateUrl: string; fields: FormData | null };

/**
 * The cart fields a `/cart/checkout` link carries in its query — `note`, `attributes[…]`,
 * `discount` (`linkUpdateFields`) — as an update for `{cart}/update.js` before the handoff
 * (D372): Intelligems writes `attributes[igId]` onto such a link. `null` for any other link, or
 * one with nothing to carry (then `cart.js` is read, as for `/checkout`).
 */
function linkPost(link: string | null): CartFormPost | null {
  const cart = cartCheckoutLinkOf(link, window.location.origin);
  if (cart === null) return null;
  const fields = linkUpdateFields(cart.search);
  if (fields.length === 0) return null;
  const data = new FormData();
  for (const [name, value] of fields) data.append(name, value);
  return { form: null, updateUrl: cart.cartPath + '/update.js', fields: data };
}

/** Raise `nativeNavigation` for a moment: a navigation of ours to Shopify's checkout. */
function letThrough(): void {
  // The Navigation API fires `navigate` synchronously inside `assign`; the flag stays up for a
  // moment longer in case a browser dispatches it later (a form submission is planned
  // asynchronously), then drops so a blocked navigation does not disable the safety net for the
  // rest of the page.
  nativeNavigation = true;
  setTimeout(() => {
    nativeNavigation = false;
  }, 1000);
}

/**
 * Leave the shopper to Shopify's checkout. **Only for a store a person switched off** (D385,
 * `./outcome.ts`): every call is behind `configState === 'disabled'`, a `disabled` outcome, or
 * `not_configured`, where no Peak exists to go to; `tests/unit/storefront/always-peak-script.test.ts`
 * holds every call site to that. Never add a call for a failure: failures get `showNotice`.
 *
 * A taken-over cart-form post is submitted to Shopify as it was (D255) — its `checkout` field makes
 * Shopify apply the fields and open its checkout, exactly the native outcome; otherwise the
 * intercepted link's own href when it was a checkout link (already checked to be same-origin and
 * checkout-shaped by `isCheckoutHref`), so a cart app's `/cart/c/{token}?key=…` keeps its key and a
 * `/cart/checkout?attributes[…]=…` its query (D372); Shopify's `/checkout` for everything else.
 */
function goNative(link: string | null, post: CartFormPost | null = null): void {
  letThrough();
  // A form a cart drawer re-rendered away meanwhile cannot be submitted (the browser drops a
  // disconnected form's submission): `/checkout` then, so the shopper is never left in place.
  const form = post === null ? null : post.form;
  if (form === null || !form.isConnected) {
    window.location.assign(link ?? PEAK_NATIVE_CHECKOUT_PATH);
    return;
  }
  // A `checkout` that came from a submitter is not in a plain `submit()`'s data: add it.
  if (!new FormData(form).has('checkout')) {
    const field = document.createElement('input');
    field.type = 'hidden';
    field.name = 'checkout';
    form.appendChild(field);
  }
  // The prototype's: an `<input name="submit">` shadows `form.submit`. Fires no `submit` event.
  HTMLFormElement.prototype.submit.call(form);
}

/** A checkout that did not reach Peak (or a store off): one console line, one report (D247). */
function reportOutcome(
  reason: string,
  kind: PeakControlKind,
  link: string | null,
  status?: number,
) {
  warn(reason);
  // Sent before any navigation, synchronously; the beacon outlives the page (D247).
  reporter?.report(reason, kind, checkoutLinkKind(link, window.location.origin), status);
}

/** A cart read (D385): the cart, or the reason code and the HTTP status of the failed read. */
type CartRead = { cart: Record<string, unknown> } | { reason: string; status?: number | undefined };

/**
 * One cart read. `cart_unavailable`: no answer, or a 2xx that is not JSON (a theme or app page where
 * the cart should be). `cart_empty_or_unreadable`: a non-2xx, or JSON that is not a cart object. The
 * status travels to the fallback report, so production shows the cause (an empty cart is returned,
 * for `handoff` to tell apart, D375).
 */
async function readCart(url: string, init: RequestInit, totalMs: number): Promise<CartRead> {
  let answer: Answer;
  try {
    answer = await fetchJson(url, init, CART_ATTEMPT_MS, totalMs, retryRead);
  } catch (error) {
    warn('cart_unavailable', error);
    return { reason: 'cart_unavailable' };
  }
  const { status, body } = answer;
  const success = status > 199 && status < 300;
  if (success && body === undefined) return { reason: 'cart_unavailable', status };
  return success && typeof body === 'object' && body !== null && !Array.isArray(body)
    ? { cart: body as Record<string, unknown> }
    : { reason: 'cart_empty_or_unreadable', status };
}

/**
 * The shopper's cart, for the handoff. Reading it was the largest cause of shoppers lost (D385), so
 * it recovers before it gives up, all inside `CART_TOTAL_MS`:
 *
 * 1. For a cart-form post whose fields must reach the cart first, or a `/cart/checkout` link whose
 *    query carries some (D372): `POST {action}/update.js`, which answers with the updated cart
 *    ("The JSON of the cart", https://shopify.dev/docs/api/ajax/reference/cart, read 2026-10-16; a
 *    `FormData` body is the documented example). It can fail for a cart that is perfectly good: a
 *    quantity above the stock is a 422 (same reference, "Error responses", and changelog
 *    "[Cart AJAX API] Inventory error message updates", which names `update.js`; read 2026-10-31),
 *    and Dawn's checkout button posts every line's quantity. So a failed update goes on with
 *    `cart.js`, reported as `cart_update_failed` with its status, and that post is not sent again:
 *    "repeated failed attempts … are throttled and return a 429 error" (same reference).
 * 2. `GET cart.js` at the store's locale-aware URL.
 * 3. A locale URL that fails: `/cart.js` at the root, the same session's cart, reported as
 *    `cart_locale_failed` with the locale URL's status.
 * 4. Still nothing while the `cart` cookie says there is a cart: again, with a growing pause, until
 *    the ceiling. Without the cookie there may be no cart to read, so the read ends there.
 *
 * One retry per request as well (`retryRead`): a read, and an update that sets absolute quantities,
 * a note and attributes, are both safe to repeat.
 */
async function loadCart(
  bootstrap: Bootstrap,
  post: CartFormPost | null,
  link: string | null,
  kind: PeakControlKind,
): Promise<CartRead> {
  const deadline = Date.now() + CART_TOTAL_MS;
  const headers = { Accept: 'application/json' };
  if (post !== null && post.fields !== null) {
    // Same origin as the shop, and the cart is tied to the customer's session cookie. One attempt's
    // time at most, so a hanging update leaves `cart.js` its turn.
    const updated = await readCart(
      post.updateUrl,
      { method: 'POST', credentials: 'same-origin', headers, body: post.fields },
      CART_ATTEMPT_MS,
    );
    if ('cart' in updated) return updated;
    reportOutcome('cart_update_failed', kind, link, updated.status);
    post.fields = null;
  }

  const get: RequestInit = { method: 'GET', credentials: 'same-origin', headers };
  const atLocale = bootstrap.cartUrl !== PEAK_DEFAULT_CART_URL;
  for (let pause = 2 * RETRY_DELAY_MS; ; pause *= 2) {
    const left = deadline - Date.now();
    // A locale URL that hangs leaves the root URL its turn, like the update above.
    const read = await readCart(
      bootstrap.cartUrl,
      get,
      atLocale ? Math.min(CART_ATTEMPT_MS, left) : left,
    );
    if ('cart' in read) return read;
    if (atLocale && deadline > Date.now()) {
      const root = await readCart(PEAK_DEFAULT_CART_URL, get, deadline - Date.now());
      if ('cart' in root) {
        reportOutcome('cart_locale_failed', kind, link, read.status);
        return root;
      }
    }
    if (
      cartTokenFromCookie(document.cookie) === undefined ||
      deadline - Date.now() < pause + MIN_RETRY_MS
    ) {
      return read;
    }
    await new Promise((resolve) => setTimeout(resolve, pause));
  }
}

async function handoff(
  bootstrap: Bootstrap,
  config: Pick<YcStorefrontConfig, 'serviceUrl' | 'experiments' | 'trackbee'>,
  link: string | null,
  kind: PeakControlKind,
  post: CartFormPost | null,
): Promise<YcOutcome> {
  const read = await loadCart(bootstrap, post, link, kind);
  if (!('cart' in read)) return { kind: 'failed', ...read };
  const { cart } = read;
  // An empty cart (D375): nothing to pay for, on Peak or anywhere — go to the cart page of the
  // store's locale (`{cart}.js` without `.js`). A link naming a cart of its own too (D385).
  if (!isUsableCart(cart)) {
    return { kind: 'cart', reason: 'cart_empty', url: bootstrap.cartUrl.replace(/\.js$/, '') };
  }

  // A `/cart/c/{token}` link (D204) or a `/checkouts/…` URL (D259) names a cart. If it is not the
  // session cart `cart.js` just returned, the session cart still goes to Peak (D385): Shopify's
  // checkout is no way out. The report says so, whichever way this attempt ends.
  const linkToken = linkTokenOf(link, window.location.origin);
  if (linkToken !== null && !isSameCartToken(linkToken.token, cart.token, linkToken.strict)) {
    reportOutcome(linkToken.mismatch, kind, link);
  }

  // The GA4 cookies and the Google Ads click-id cookies are only readable here, on the shop's
  // own domain (`./ga-cookies.ts`, `./google-click-cookies.ts`). `readAnalyticsCookies` never
  // throws, and `buildCreatePayload` leaves the field out when there is nothing to send.
  const analytics = readAnalyticsCookies(document);

  // The shopper's Shopify market, rendered onto our tag by the theme's Liquid (`./market.ts`,
  // D210), plus the absolute cart URL just fetched, so the checkout's links back to the store
  // keep the market prefix. `undefined` only when neither is there.
  const market = withCartUrl(readMarketContext(findScript()), bootstrap.cartUrl, location.href);

  // The A/B-experiment unit id and QA forced arm (`./experiment.ts`, D239). The unit only when
  // the store runs an experiment (D244). Consent is asked again here: withdrawn → the cookie is
  // deleted and no unit is sent. Never throws.
  const experiment = readExperimentHint(experimentEnv(), storage(), config.experiments);

  // TrackBee's visitor id and the shopper's consent (`./trackbee.ts`, D367) — only for a store
  // with TrackBee on, read out of the shop's own `localStorage`, never throwing.
  const trackbee =
    config.trackbee === true ? readTrackBeeHint(local(), shopifyGlobal()) : undefined;

  try {
    // One retry on a network error, 502, 503 or 504 (`retryCreate`, D255). A repeat from this
    // browser — same IP, User-Agent, cart token and lines, within 15 minutes — is handed the
    // checkout the first attempt created, if it got that far (`decideReuse`,
    // `src/lib/checkout/create/reuse.ts`); a 4xx and a timeout are never repeated.
    const answer = await fetchJson(
      createEndpointUrl(config.serviceUrl),
      {
        method: 'POST',
        mode: 'cors',
        // No cookies cross-origin: the handoff is authenticated by the public key plus the
        // server-side shop-domain and Origin checks, not by an ambient session.
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(
          buildCreatePayload(
            bootstrap.publicKey,
            bootstrap.shopDomain,
            cart,
            analytics,
            market,
            experiment,
            earlyTap,
            trackbee,
          ),
        ),
      },
      CREATE_ATTEMPT_MS,
      CREATE_TOTAL_MS,
      retryCreate,
    );

    // A body we cannot parse (`undefined`) is handled by the decision table as `no_redirect_url`.
    return decideOutcome(answer.status, answer.body);
  } catch (error) {
    warn('create_unavailable', error);
    return { kind: 'failed', reason: 'create_unavailable' };
  }
}

/**
 * Shared by the click, submit and navigate paths. `link` is the checkout URL when the caller
 * already knows it (a scripted navigation has no anchor); otherwise it is read off `control`.
 * While the config is still loading the action is held for it (`start`, D255).
 */
function intercept(
  event: Event,
  control: Element | null,
  bootstrap: Bootstrap,
  link: string | null = control === null ? null : checkoutLinkOf(control),
): void {
  if (!active()) return;

  const kind = controlKindOf(event.type, control instanceof HTMLAnchorElement, control !== null);
  // A checkout button that submits a cart form (Dawn's `form="cart"`, Debut's nested button):
  // the form's own `updates[]` / `note` reach the cart first, as Shopify's post would apply them
  // (D259, `takeForm`). Not on a scripted navigation, whose source only carries the busy state.
  const post = event.type === 'navigate' || control === null ? null : submitterPost(control);
  if (post !== null) {
    takeForm(event, post, control, bootstrap, kind);
    return;
  }

  event.preventDefault();
  event.stopImmediatePropagation();

  start(control, bootstrap, link, kind);
}

/**
 * The handoff and what follows it, shared by `intercept`, `takeForm`, the new-tab marker page
 * (`resumeFromMarker`, D254) and the notice's "Try again". `control` only carries the busy state.
 * It first waits for the config when that has not answered (`configured`, D255): `enabled: false`
 * then is the store being off, `config_wait_inactive`, to the same native destination; a config
 * that cannot be read goes on with the bootstrap's service (D375). A failure gets one more
 * automatic attempt when that is worth it (`isRepeatable`, `REPEAT_WITHIN_MS`), the control still
 * busy, and only the last attempt is reported.
 */
function start(
  control: Element | null,
  bootstrap: Bootstrap,
  link: string | null,
  kind: PeakControlKind,
  post: CartFormPost | null = null,
): void {
  if (inFlight) return;
  inFlight = true;
  if (control !== null) setBusy(control, true);

  // A `/cart/checkout` link's query fields reach the cart first (D372); a form post has its own.
  const update = post ?? linkPost(link);
  const began = Date.now();

  const attempt = (): Promise<YcOutcome> =>
    configured(bootstrap)
      .then((config): YcOutcome | Promise<YcOutcome> =>
        configState === 'disabled'
          ? { kind: 'disabled', reason: 'config_wait_inactive' }
          : handoff(
              bootstrap,
              // D375: no config to read, so no experiment unit and no TrackBee id.
              config ?? { serviceUrl: bootstrap.serviceUrl, experiments: false },
              link,
              kind,
              update,
            ),
      )
      .catch((error: unknown): YcOutcome => {
        warn('handoff_failed', error);
        return { kind: 'failed', reason: 'handoff_failed' };
      });

  void attempt()
    .then((outcome) =>
      isRepeatable(outcome) && Date.now() - began < REPEAT_WITHIN_MS
        ? new Promise<void>((resolve) => setTimeout(resolve, REPEAT_DELAY_MS)).then(attempt)
        : outcome,
    )
    .then((outcome) => {
      // Released before navigating: if `assign` is blocked the customer must be able to click
      // again, and a button re-enabled during an unload that does happen is invisible.
      inFlight = false;
      if (control !== null) setBusy(control, false);
      closeNotice();
      if (outcome.kind === 'redirect') window.location.assign(outcome.url);
      else {
        reportOutcome(outcome.reason, kind, link, outcome.status);
        if (outcome.kind === 'cart') window.location.assign(outcome.url);
        else if (outcome.kind === 'disabled') goNative(link, update);
        else showNotice(() => start(control, bootstrap, link, kind, post));
      }
      earlyTap = false;
    });
}

// ---------------------------------------------------------------------------
// Delegation
// ---------------------------------------------------------------------------

/** Inside a `data-peak-ignore` element, counting the hosts of any shadow roots on the way. */
function isIgnored(element: Element): boolean {
  return closestComposed(element, '[' + PEAK_OPT_OUT_ATTRIBUTE + ']') !== null;
}

/**
 * Anchors need a second look: a theme may render `<a href="/en/checkout">` or an absolute URL,
 * a cart app `/cart/c/{token}?key=…` (D204), a theme or app `/cart/checkout?…` (D372), none of
 * which the CSS selector list matches.
 * `data-peak-checkout` on the anchor wins regardless.
 *
 * Returns the checkout link, absolute and same-origin, or `null` when the element is not an
 * anchor to checkout.
 */
function checkoutLinkOf(element: Element): string | null {
  if (!(element instanceof HTMLAnchorElement)) return null;
  const href = element.getAttribute('href');
  const origin = window.location.origin;
  return isCheckoutHref(href, origin) ? new URL(href as string, origin).href : null;
}

/**
 * The checkout control a pointer event is on, exactly as the interceptor matches it — shared by
 * `onClick`, the new-tab gestures (`onNewTabGesture`) and the passive observer
 * (`observeInactive`), so they can never disagree about what a checkout control is. `ignored`
 * inside `data-peak-ignore`; `null` for everything else. Which buttons and modifiers count is
 * the caller's to decide — except Alt, a download, which is never ours (D254).
 */
function clickedControl(event: Event): Element | 'ignored' | null {
  if (event.defaultPrevented || (event as MouseEvent).altKey) return null;

  // The real node, even inside an open shadow root (UpCart), where `event.target` is the host.
  const target = originalTarget(event);
  if (target === null) return null;
  if (isIgnored(target)) return 'ignored';

  const control = resolveCheckoutControl(target as unknown as YcElementLike) as Element | null;
  if (control !== null) return control;

  const anchor = target.closest('a');
  return anchor !== null && checkoutLinkOf(anchor) !== null ? anchor : null;
}

function onClick(event: MouseEvent, bootstrap: Bootstrap): void {
  // An `href` still pointed at the marker page (a context menu that was dismissed) goes back
  // before anything reads it, so a plain click sees exactly the link it always saw (D254).
  restoreHref();
  if (event.button !== 0 || !active()) return;
  const control = clickedControl(event);
  if (control === 'ignored') {
    // Also covers the navigation an app starts from this click (`onNavigate`), for as long as
    // the click is being dispatched.
    ignoredClick = true;
    setTimeout(() => {
      ignoredClick = false;
    }, 0);
    return;
  }
  if (control === null) {
    takePermalink(event, bootstrap);
    return;
  }
  if (!wantsNewTab(event)) {
    intercept(event, control, bootstrap);
    return;
  }

  // ⌘/Ctrl/Shift-click (D254). A link: the browser opens the marker page, because that is the
  // `href` while its default action runs; no `preventDefault`. `stopImmediatePropagation` keeps
  // a cart app's own handler (UpCart's sets `location.href`) from also sending this tab to
  // checkout.
  event.stopImmediatePropagation();
  if (pointAtMarker(control)) {
    setTimeout(restoreHref, 0);
    return;
  }
  // A button, or an opt-in control that is no link: no browser opens those in a tab of their own
  // (a submit button may, into native checkout), so the marker page is opened here, inside the
  // click's user activation. A blocked popup hands off in this tab instead, as a plain click.
  const tab = window.open(newTabMarkerUrl(window.location, null), '_blank');
  if (tab === null) {
    intercept(event, control, bootstrap);
    return;
  }
  event.preventDefault();
  try {
    tab.opener = null;
  } catch {
    // Same origin, so nothing to protect; the null is hygiene.
  }
}

/** The same-origin cart permalink anchor an event is on, or `null`. */
function permalinkAt(event: Event): { anchor: Element; href: string; link: PeakPermalink } | null {
  const target = originalTarget(event);
  const anchor = target === null ? null : target.closest('a');
  if (anchor === null) return null;
  const href = anchor.getAttribute('href');
  const link = permalinkOf(href, window.location.origin);
  return link === null ? null : { anchor, href: href as string, link };
}

/**
 * The Peak buy link for a permalink. Without a config (still loading, unreadable) it uses the
 * bootstrap's service (D385): the buy link applies every availability rule itself. TrackBee's
 * visitor id rides on it as `_t_i` / `_t_i_ts` for a store with TrackBee on (D367): the route
 * reads them like the create endpoint reads `trackbee`.
 */
function buyLinkOf(permalink: PeakPermalink, bootstrap: Bootstrap): string {
  const url = buyLinkHref(
    activeConfig === null ? bootstrap.serviceUrl : activeConfig.serviceUrl,
    bootstrap.publicKey,
    permalink,
  );
  return activeConfig !== null && activeConfig.trackbee === true
    ? withTrackBeeVisitor(url, readTrackBeeVisitor(local()))
    : url;
}

/**
 * A click on a same-origin cart permalink (`/cart/{variant}:{qty},…`) goes to the Peak buy link
 * for exactly that cart (D260, restored by D359, `./buy-link.ts`): a plain click in this tab, a
 * ⌘/Ctrl/Shift-click in a new one. While the config is still loading a plain click is held like a
 * checkout click (D255); a config that cannot be read uses the bootstrap's service (D385), and only
 * an `enabled: false` answer leaves the permalink to Shopify. A middle click, the context menu and
 * a drag get the buy link too (`onNewTabGesture`). A permalink tap an installed `stub-3` held before
 * this script ran is replayed as a click and lands here too (`replayEarlyTap`, D359).
 */
function takePermalink(event: MouseEvent, bootstrap: Bootstrap): void {
  const at = permalinkAt(event);
  if (at === null) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  followPermalink(at.anchor, at.href, at.link, wantsNewTab(event), bootstrap);
}

/**
 * Send a permalink to its buy link, or to the permalink itself when the config answered
 * `enabled: false` meanwhile (`config_wait_inactive`, link `permalink`). `anchor` carries the busy
 * mark while the config loads; a disconnected one (an early tap whose link the page re-rendered
 * away) is harmless. A new tab cannot wait: it needs the URL inside the click's user activation.
 */
function followPermalink(
  anchor: Element,
  href: string,
  permalink: PeakPermalink,
  newTab: boolean,
  bootstrap: Bootstrap,
): void {
  const go = (): void => {
    if (!active()) {
      reporter?.report('config_wait_inactive', 'anchor', 'permalink');
      goNative(new URL(href, window.location.origin).href);
      return;
    }
    const url = buyLinkOf(permalink, bootstrap);
    if (newTab) {
      window.open(url, '_blank', 'noopener');
      return;
    }
    letThrough();
    window.location.assign(url);
  };
  if (activeConfig !== null || newTab) {
    go();
    return;
  }
  setBusy(anchor, true);
  void configured(bootstrap).then(() => {
    setBusy(anchor, false);
    go();
  });
}

/** Put a swapped `href` back (D254); a no-op when none is swapped or an app replaced it since. */
function restoreHref(): void {
  if (swapped !== null && swapped.anchor.getAttribute('href') === swapped.marker) {
    swapped.anchor.setAttribute('href', swapped.href);
  }
  swapped = null;
}

/** Point an anchor's `href` at `url` until `restoreHref` (D254). */
function swapHref(anchor: HTMLAnchorElement, href: string, url: string): void {
  restoreHref();
  anchor.setAttribute('href', url);
  swapped = { anchor, href, marker: url };
}

/**
 * Point a checkout anchor's `href` at the new-tab marker page, carrying its checkout link
 * (D254). `false`, and nothing changed, for a control that is not a link with an `href` — a
 * browser opens nothing in a new tab from those.
 */
function pointAtMarker(control: Element): boolean {
  if (!(control instanceof HTMLAnchorElement)) return false;
  const href = control.getAttribute('href');
  if (href === null) return false;
  swapHref(control, href, newTabMarkerUrl(window.location, checkoutLinkOf(control)));
  return true;
}

/**
 * The other ways a link is opened in a new tab or window (D254), each pointing the checkout
 * anchor at the marker page, or a cart permalink at its buy link (D385), before the browser reads
 * its `href`:
 *
 * - `mousedown` of a middle or ⌘/Ctrl/Shift press — early, for a browser that reads the link
 *   before `auxclick`/`click`; those swap again anyway.
 * - `auxclick` of the middle button — the new tab opens in its default action; the `href` is
 *   restored on the next tick.
 * - `contextmenu` — "Open link in new tab/window", "Copy link address" (a copied marker URL
 *   still ends in Peak). The menu stays open as long as the shopper likes, so the `href` is
 *   restored on the next `mousedown` or `keydown` instead, before any click can read it.
 * - `dragstart` — a link dropped on the tab strip: the drag data is the marker URL.
 *
 * Every event first restores a previous swap. Whenever the script is `active`: the marker page
 * waits for its own config, and only an `enabled: false` answer leaves it to Shopify.
 */
function onNewTabGesture(event: Event, bootstrap: Bootstrap): void {
  restoreHref();
  if (!active() || event.type === 'keydown') return;
  if (
    (event.type === 'mousedown' || event.type === 'auxclick') &&
    !wantsNewTab(event as MouseEvent)
  ) {
    return;
  }
  const control = clickedControl(event);
  const permalink = control === null ? permalinkAt(event) : null;
  const anchor = permalink === null ? control : permalink.anchor;
  if (!(anchor instanceof HTMLAnchorElement)) return;
  const href = anchor.getAttribute('href');
  if (href === null) return;
  const url =
    permalink === null
      ? newTabMarkerUrl(window.location, checkoutLinkOf(anchor))
      : buyLinkOf(permalink.link, bootstrap);
  if (event.type !== 'dragstart') {
    swapHref(anchor, href, url);
    if (event.type === 'auxclick') setTimeout(restoreHref, 0);
    return;
  }
  const data = (event as DragEvent).dataTransfer;
  if (data === null) return;
  const absolute = new URL(url, location.href).href;
  try {
    data.setData('text/uri-list', absolute);
    data.setData('text/plain', absolute);
  } catch {
    // A drag the browser will not let us write to keeps the original link.
  }
}

/** The part of the Navigation API's `NavigateEvent` read here (not in TypeScript's DOM lib yet). */
type NavigateEventLike = Event & {
  navigationType?: string;
  destination?: { url?: string };
  hashChange?: boolean;
  downloadRequest?: string | null;
  formData?: unknown;
  sourceElement?: Element | null;
};

/**
 * The safety net for scripted navigation (D212): a cart app that sets `location.href` to
 * `/checkout`, `/cart/checkout` (D372), `/cart/c/{token}` or any `/checkouts/…` (D385) from a
 * handler we did not see (a closed shadow root, a capture listener on `window`, a timer) is taken
 * over here, where the browser has the Navigation API. Only a cancellable push/replace to one of
 * those same-origin paths (`isCheckoutHref`, so never a page after payment), never a reload,
 * back/forward, download or hash change; never our own way out (`nativeNavigation`), never from a
 * `data-peak-ignore` click, never while disabled. A jump from a flow with a cart of its own (a "Buy
 * it now" app) hands off the session cart instead, or lands on the cart page (`handoff`): the
 * tradeoff the owner took with D385.
 *
 * Plus one form post (D255): a POST to the cart whose data carries `checkout` — a theme's
 * `form.submit()`, which fires no `submit` event, so only this event sees it. Chromium 152
 * (2026-10-16) dispatches it as a cancellable `push` to `/cart` with `formData` (the hidden
 * `checkout` included) and the form as `sourceElement`; a submitter, when there is one, is the
 * `sourceElement` instead. It is taken exactly like a submit (`takeForm`); any other form post is
 * let through.
 */
function onNavigate(event: NavigateEventLike, bootstrap: Bootstrap): void {
  if (!active() || nativeNavigation || ignoredClick) return;
  if (event.defaultPrevented || event.hashChange === true) return;
  if (event.navigationType !== 'push' && event.navigationType !== 'replace') return;

  const url = event.destination?.url ?? null;
  const source = event.sourceElement instanceof Element ? event.sourceElement : null;
  const data = event.formData;
  if (data instanceof FormData && event.cancelable && event.downloadRequest == null) {
    const form =
      source instanceof HTMLFormElement
        ? source
        : source instanceof HTMLButtonElement || source instanceof HTMLInputElement
          ? source.form
          : null;
    const updateUrl = cartUpdateUrlOf(url, location.href);
    if (
      form !== null &&
      source !== null &&
      updateUrl !== null &&
      data.has('checkout') &&
      !isIgnored(source) &&
      !isIgnored(form)
    ) {
      const submitter = source === form ? null : source;
      takeForm(event, { form, updateUrl, data }, submitter, bootstrap);
      return;
    }
  }

  if (
    !event.cancelable ||
    event.downloadRequest != null ||
    data != null ||
    !isCheckoutHref(url, window.location.origin)
  ) {
    // Diagnostics only (D247): a navigation into checkout that is let through although the
    // script is active — a form post with no cart form behind it, a non-cancellable one. Once per
    // page load; our own way out never gets here (`nativeNavigation`).
    const link = checkoutLinkKind(url, window.location.origin);
    if (link !== 'none') {
      reporter?.reportOnce('navigation', 'navigation_not_intercepted', 'navigation', link);
    }
    return;
  }

  if (source !== null && isIgnored(source)) return;

  intercept(event, source, bootstrap, new URL(url as string).href);
}

/**
 * Programmatic submits.
 *
 * The click handler already stops the ordinary path, but some themes call
 * `form.requestSubmit(button)` from their own JavaScript. A submit whose submitter is a
 * checkout control is intercepted as a click; one without such a submitter only when it is a
 * checkout-bound cart-form post (`checkoutFormPost`, D255) — intercepting every cart-form submit
 * would break the "update cart" button and quantity changes, which post to the same action.
 */
function submittedControl(event: SubmitEvent): Element | null {
  if (event.defaultPrevented) return null;

  const submitter = event.submitter;
  if (submitter === null || submitter === undefined) return null;

  const control = resolveCheckoutControl(submitter as unknown as YcElementLike) as Element | null;
  return control === null || isIgnored(control) ? null : control;
}

/** A cart-form post: the form, its `update.js` URL and its data (`checkoutFormPost`). */
type FormPost = { form: HTMLFormElement; updateUrl: string; data: FormData };

/**
 * A submit of a Shopify cart form (`POST` to `/cart`, locale prefix allowed, same origin) that
 * Shopify would turn into its checkout although no checkout control submitted it (D255):
 * `checkout` in its data (a hidden field, a theme's `requestSubmit()` with no button), or a
 * submitter named `checkout` that the selector list does not match. `null` for every other
 * submit, and inside `data-peak-ignore`.
 */
function checkoutFormPost(event: SubmitEvent): FormPost | null {
  const form = event.target;
  const submitter = event.submitter ?? null;
  if (event.defaultPrevented || !(form instanceof HTMLFormElement)) return null;
  const post = cartFormPost(form, submitter);
  const named = submitter !== null && submitter.getAttribute('name') === 'checkout';
  return post !== null && (post.data.has('checkout') || named) ? post : null;
}

/**
 * `form` submitted by `submitter` as a Shopify cart-form post: same origin, `POST`, action `/cart`
 * (locale prefix allowed; the submitter's `formaction` / `formmethod` win over the form's, as in
 * the browser), neither inside `data-peak-ignore`. `null` for anything else.
 */
function cartFormPost(form: HTMLFormElement, submitter: Element | null): FormPost | null {
  if (isIgnored(form) || (submitter !== null && isIgnored(submitter))) return null;
  const attribute = (name: string): string | null =>
    (submitter === null ? null : submitter.getAttribute('form' + name)) || form.getAttribute(name);
  const updateUrl = cartUpdateUrlOf(attribute('action'), location.href);
  if (updateUrl === null || (attribute('method') || '').toLowerCase() !== 'post') return null;
  return { form, updateUrl, data: new FormData(form) };
}

/**
 * The cart-form post a checkout control would submit (D259): a submit button or input whose form
 * — nested, or by `form="…"` as Dawn's — is a cart-form post (`cartFormPost`); `null` for a link,
 * a `type="button"` (Kaching's: it submits nothing) and every other control.
 */
function submitterPost(control: Element): FormPost | null {
  const submits =
    (control instanceof HTMLButtonElement && control.type === 'submit') ||
    (control instanceof HTMLInputElement &&
      (control.type === 'submit' || control.type === 'image'));
  const form = submits ? (control as HTMLButtonElement | HTMLInputElement).form : null;
  return form === null ? null : cartFormPost(form, control);
}

/**
 * Take a checkout-bound cart-form post (D255): every one goes to Peak (D327). Its fields that
 * `/cart/update.js` documents — `updates[…]`, `note`, `attributes[…]`, `discount` — reach the cart
 * first (`loadCart`); every other field is dropped (`cartUpdateFields`), so line properties, a
 * selling plan, `items[…]`, a `goto_pp` / `return_to` redirect and a file posted with the form are
 * lost to the checkout (until D327 they left the post to Shopify). Only a store that turns out to
 * be switched off submits the form to Shopify as it was (`goNative`), every field included.
 *
 * Also the path of a checkout button that submits a cart form (D259, `intercept`, `kind` its
 * own): its click is cancelled like the submit it would become.
 */
function takeForm(
  event: Event,
  post: FormPost,
  submitter: Element | null,
  bootstrap: Bootstrap,
  kind: PeakControlKind = 'form',
): void {
  event.preventDefault();
  event.stopImmediatePropagation();

  const entries: [string, unknown][] = [];
  post.data.forEach((value, name) => {
    entries.push([name, value]);
  });
  const fields = cartUpdateFields(
    entries,
    submitter === null ? null : submitter.getAttribute('name'),
  );
  // No field to carry (a drawer form with no quantity inputs): `cart.js`, no extra round trip.
  let carried: FormData | null = null;
  if (fields.length > 0) {
    carried = new FormData();
    for (const [name, value] of fields) carried.append(name, value);
  }
  start(submitter ?? post.form, bootstrap, null, kind, {
    form: post.form,
    updateUrl: post.updateUrl,
    fields: carried,
  });
}

function onSubmit(event: SubmitEvent, bootstrap: Bootstrap): void {
  if (!active()) return;
  const control = submittedControl(event);
  if (control !== null) {
    intercept(event, control, bootstrap);
    return;
  }
  const post = checkoutFormPost(event);
  if (post !== null) takeForm(event, post, event.submitter ?? null, bootstrap);
}

/**
 * The passive observer (D247), attached at init — before the config answers — and never
 * removed. It only looks: no `preventDefault`, no `stopPropagation`. A checkout click, submit or
 * scripted navigation while the script is **not** active (`enabled: false`) is the one
 * native-checkout path that would otherwise leave no trace anywhere; it is reported once per page
 * load. While the script is active (`active`: everything else) it does nothing: the interceptor
 * owns the event and reports its own outcome (`createFallbackReporter`).
 */
function observeInactive(event: Event): void {
  try {
    // `nativeNavigation`: our own way out, already reported by `start`, when a `reinit` switched
    // the config off mid-handoff.
    if (reporter === null || active() || nativeNavigation) return;
    const origin = window.location.origin;
    let control: Element | null = null;
    let link: string | null = null;
    let kind: PeakControlKind | null = null;
    if (event.type === 'click') {
      if ((event as MouseEvent).button !== 0) return;
      const clicked = clickedControl(event);
      if (clicked === null || clicked === 'ignored') return;
      control = clicked;
      link = checkoutLinkOf(clicked);
    } else if (event.type === 'submit') {
      control = submittedControl(event as SubmitEvent);
      if (control === null) {
        if (checkoutFormPost(event as SubmitEvent) === null) return;
        kind = 'form';
      }
    } else {
      const navigation = event as NavigateEventLike;
      if (navigation.hashChange === true) return;
      if (navigation.navigationType !== 'push' && navigation.navigationType !== 'replace') return;
      link = navigation.destination?.url ?? null;
      if (!isCheckoutHref(link, origin)) return;
    }
    reporter.observeInactive(
      kind ?? controlKindOf(event.type, control instanceof HTMLAnchorElement, control !== null),
      checkoutLinkKind(link, origin),
    );
  } catch {
    // Observing must never cost the shopper anything.
  }
}

/**
 * The delegated listeners, attached once at init — before the config answers (D255), so a
 * checkout used while it loads can be held. Each one stands down while the script is not
 * `active`, which leaves the page exactly as Shopify shipped it.
 */
function attachListeners(bootstrap: Bootstrap): void {
  // Capture phase, on `window` and on `document` (D259): `window` is the first stop of every
  // event's capture path, so we run before any app listener on `document` — capture or not, early
  // or late — and before every `window` capture listener registered after this script ran;
  // `stopImmediatePropagation` in `intercept` then keeps every one of them from running. The
  // `document` copy changes nothing where `window` already ran (`once`), and is there for a
  // page whose `window` listener never fires. Delegation also means a cart drawer rendered five
  // minutes from now is covered with no observer. Limitation: a `window` capture listener an app
  // registered before this `defer` script ran still runs first and can navigate first.
  const seen = new WeakSet<Event>();
  const once =
    (handler: (event: Event) => void) =>
    (event: Event): void => {
      if (seen.has(event)) return;
      seen.add(event);
      handler(event);
    };
  const both = (name: string, handler: (event: Event) => void): void => {
    const listener = once(handler);
    window.addEventListener(name, listener, true);
    document.addEventListener(name, listener, true);
  };
  both('click', (event) => onClick(event as MouseEvent, bootstrap));
  both('submit', (event) => onSubmit(event as SubmitEvent, bootstrap));
  // A checkout link opened in a new tab or window (D254).
  for (const name of ['mousedown', 'auxclick', 'contextmenu', 'dragstart', 'keydown']) {
    both(name, (event) => onNewTabGesture(event, bootstrap));
  }

  // Feature-detected; where the Navigation API is missing this is simply not attached (D212).
  const navigation = (window as unknown as { navigation?: EventTarget }).navigation;
  if (navigation !== undefined && typeof navigation.addEventListener === 'function') {
    navigation.addEventListener('navigate', (event) =>
      onNavigate(event as NavigateEventLike, bootstrap),
    );
  }

  // UpCart builds its shadow root when it mounts, which may be after the config answered — or,
  // with the first-paint hiding (D259), before it.
  for (const name of UPCART_RENDER_EVENTS) {
    window.addEventListener(name, () => {
      if (expressHidden) applyExpressEverywhere(true);
    });
  }
}

// ---------------------------------------------------------------------------
// The new-tab marker page (D254)
// ---------------------------------------------------------------------------

/**
 * This page's `#peak-checkout` marker (`./new-tab.ts`), taken out of the address bar at once with
 * `history.replaceState` — path and query kept, history state kept — so a reload, a back to this
 * page or a bookmark never hands off again, and a later pixel does not log a carried `?key=`.
 * `null` when there is none.
 */
function takeMarker(): { link: string | null } | null {
  const marker = readNewTabMarker(window.location.hash, window.location.origin);
  if (marker !== null) {
    try {
      history.replaceState(history.state, '', location.pathname + location.search);
    } catch {
      // An address bar we cannot rewrite keeps the fragment; a reload would hand off again.
    }
  }
  return marker;
}

/**
 * A page opened as a checkout in a new tab: once the config has answered, the same handoff as a
 * click — no control, the carried link (or `null`) for the token check — with `<html>` as the
 * busy element, so a theme can style `html[data-peak-state="loading"]`. Only a store switched off
 * (`enabled: false`) goes straight on to the native checkout, reported as `script_inactive`; an
 * unreadable config hands off like a click (`start`, D385). Never produces another marker. Also an
 * early tap whose control the page has removed meanwhile (`replayEarlyTap`, its own `kind`).
 */
function resumeFromMarker(
  bootstrap: Bootstrap,
  link: string | null,
  kind: PeakControlKind = 'new_tab',
): void {
  if (active()) {
    start(document.documentElement, bootstrap, link, kind);
    return;
  }
  reporter?.observeInactive(kind, checkoutLinkKind(link, window.location.origin));
  goNative(link);
}

// ---------------------------------------------------------------------------
// The early tap (D343; kept for installed stubs since D360)
// ---------------------------------------------------------------------------

/**
 * The tap an installed early-tap stub held, if any; the stub's listeners are released either way.
 * `null` on a theme without the stub — every store installed since D360.
 */
function takeEarlyTap(): PeakEarlyTap | null {
  try {
    const hook = (window as YcWindow).__peakCheckoutEarly;
    return hook && typeof hook.take === 'function' ? hook.take() : null;
  } catch {
    return null;
  }
}

/**
 * Replay a tap an installed early-tap stub held before this script ran (D343): the control is
 * clicked again, or the form submitted again with its submitter, so the listeners attached at init
 * give it exactly what a live tap gets — the handoff with its config wait, the cart-form path, and
 * while the script is not active (`enabled: false`) the native checkout with its usual report. A
 * control or form the page removed meanwhile, or a browser without `requestSubmit`: the handoff
 * straight away (`resumeFromMarker`), native only without a bootstrap (`not_configured`, where no
 * Peak exists to go to). A held cart permalink (stub-3, D359) is clicked again the same way, so
 * `onClick` sends it to its buy link (`takePermalink`); one the page removed meanwhile goes to its
 * buy link directly (`followPermalink`), or to the permalink itself when the store is off.
 * `earlyTap` marks the requests and reports that follow.
 */
function replayEarlyTap(tap: PeakEarlyTap | null, bootstrap: Bootstrap | null): void {
  if (tap === null) return;
  earlyTap = true;
  try {
    const { control, form } = tap;
    // A held cart permalink (stub-3, D359): an anchor whose `href` is `/cart/{variant}:{qty},…`.
    const permalinkHref =
      form === null &&
      control instanceof HTMLAnchorElement &&
      permalinkOf(control.getAttribute('href'), window.location.origin) !== null
        ? control.getAttribute('href')
        : null;
    if (form === null ? control?.isConnected : form.isConnected && 'requestSubmit' in form) {
      if (form === null) (control as HTMLElement).click();
      else {
        try {
          form.requestSubmit(tap.submitter);
        } catch {
          // A submitter that is no longer the form's: the post without it.
          form.requestSubmit();
        }
      }
    } else if (permalinkHref !== null) {
      // A permalink the page re-rendered away (D359): its own buy link — `followPermalink` also
      // handles a store that is off — never the shopper's `cart.js`, which is another cart.
      const permalink = permalinkOf(permalinkHref, window.location.origin);
      if (bootstrap === null || permalink === null) {
        goNative(new URL(permalinkHref, window.location.origin).href);
      } else followPermalink(control as Element, permalinkHref, permalink, false, bootstrap);
    } else {
      const link = control === null ? null : checkoutLinkOf(control);
      if (bootstrap === null) goNative(link);
      else {
        resumeFromMarker(
          bootstrap,
          link,
          tap.type === 'submit' && control === null
            ? 'form'
            : controlKindOf(tap.type, control instanceof HTMLAnchorElement, control !== null),
        );
      }
    }
  } finally {
    // Nothing above throws in practice (`click()` swallows a theme handler's exception); were it
    // to, the listeners are attached, so the shopper's next tap is a live one.
    if (!inFlight) earlyTap = false;
  }
}

// ---------------------------------------------------------------------------
// The load report (D344, provisional)
// ---------------------------------------------------------------------------

/**
 * One `script_ready_late` report when this script started more than 6 s after the navigation
 * (`./load-report.ts`): once per page view, since `init` runs once. A page without a bootstrap
 * sends none: there is nowhere to send it. Never throws.
 */
function reportReadyLate(bootstrap: Bootstrap, startedMs: number | null): void {
  try {
    const report = readyLateReport(
      {
        publicKey: bootstrap.publicKey,
        shopDomain: bootstrap.shopDomain,
        version: PEAK_CHECKOUT_VERSION,
      },
      {
        delayMs: startedMs,
        maxTouchPoints: navigator.maxTouchPoints || 0,
        cookie: document.cookie,
        path: window.location.pathname,
        visibility: document.visibilityState,
        stub: (window as YcWindow).__peakCheckoutEarly !== undefined,
      },
    );
    if (report === null) return;
    sendFallbackReport(
      { navigator, fetch: (url, init) => fetch(url, init) },
      fallbackEndpointUrl(bootstrap.serviceUrl),
      report,
    );
  } catch {
    // A diagnostics beacon is never worth an exception on the merchant's page.
  }
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

function init(): void {
  const win = window as YcWindow;
  if (win.__peakCheckoutLoaded === true) return;
  win.__peakCheckoutLoaded = true;
  // How late this script started, read before anything else runs (D344, `reportReadyLate`).
  const startedMs = loadDelayMs(window.performance);
  // A tap an installed stub held before this script ran (D343; themes pasted before D360): taken
  // now, so the stub's listeners are gone before ours attach, and replayed once ours are
  // (`replayEarlyTap`).
  const early = takeEarlyTap();

  // A checkout opened in a new tab (D254), read and removed before anything else looks at the URL.
  const marker = takeMarker();

  const bootstrap = readBootstrap();
  if (bootstrap === null) {
    // No key or no service: nothing here can reach Peak, so this is the one thing left.
    warn('not_configured');
    if (marker !== null) goNative(marker.link);
    replayEarlyTap(early, null);
    return;
  }
  reporter = createFallbackReporter(
    {
      publicKey: bootstrap.publicKey,
      shopDomain: bootstrap.shopDomain,
      version: PEAK_CHECKOUT_VERSION,
      customer: customerStateOf(findScript()),
    },
    () => ({ config: configState, configReason, early: earlyTap }),
    (report) =>
      sendFallbackReport(
        // `fetch` wrapped so it keeps its receiver; `sendBeacon` is called on `navigator`.
        { navigator, fetch: (url, init) => fetch(url, init) },
        fallbackEndpointUrl(activeConfig ? activeConfig.serviceUrl : bootstrap.serviceUrl),
        report,
      ),
    // D352: the page the fallback happened on and its cart, so the endpoint can keep it by token.
    () => ({
      path: window.location.pathname,
      cartToken: cartTokenFromCookie(document.cookie),
      touch: (navigator.maxTouchPoints || 0) > 1 ? true : undefined,
    }),
  );

  reportReadyLate(bootstrap, startedMs);
  // The session's last config answer, however old (D255): it decides whether a config that failed
  // is asked once more for a checkout (`configured`), and the express-button style below.
  hint = readCachedConfig(storage(), bootstrap.publicKey, Date.now(), Infinity);

  // The interceptor (D255: at init, not once the config answers — each listener stands down
  // only for `enabled: false`, `active`), then the passive observer, before the config is asked
  // for, so a click while it is in flight is seen too (D247).
  attachListeners(bootstrap);
  document.addEventListener('click', observeInactive, true);
  document.addEventListener('submit', observeInactive, true);
  const navigation = (window as unknown as { navigation?: EventTarget }).navigation;
  if (navigation !== undefined && typeof navigation.addEventListener === 'function') {
    navigation.addEventListener('navigate', observeInactive);
  }

  // A Google Ads click id in this page's landing URL is kept in Peak's own first-party cookie
  // for the handoff — for a store with no Google tag to write `_gcl_*` — gated on Shopify's
  // Customer Privacy API when the page has it (`./url-click-ids.ts`). The URL is read from the
  // navigation-timing entry first: a theme's own `defer` script may already have rewritten
  // `location` (`?variant=` pinning) by the time this one runs. Synchronous, never throws.
  captureUrlClickIds({
    search: window.location.search,
    performance: window.performance,
    secure: window.location.protocol === 'https:',
    now: Date.now(),
    document,
    shopifyGlobal: shopifyGlobal(),
  });

  // The QA forced arm from `?peak_arm=` (`./experiment.ts`, D239): kept whether or not the store
  // runs an experiment, because a draft can be tested before anything runs, then taken out of
  // the address bar so a later pixel does not log the token. The visitor id waits for the config
  // (`resolveConfig`, D244). Synchronous, never throws.
  captureForcedArm(landingSearch(window.performance, window.location.search), storage());
  stripForcedArm(window);

  // Hide the express buttons now, before the config round trip (D259, superseding D48), unless
  // the session's last answer, however old, said `enabled: false`: on the first page of a session
  // a Shop Pay or "Buy it now" tap during the fetch went native. `resolveConfig` reconciles it a
  // moment later: only an `enabled: false` answer shows them (D385), so a first-time visitor of a
  // store that is switched off sees the buttons once the config has answered.
  if (hint === null || hint.enabled) applyExpressEverywhere(true);

  win.peakCheckout = {
    version: PEAK_CHECKOUT_VERSION,
    reinit: () => {
      void resolveConfig(bootstrap, false);
    },
  };

  // The marker page — on load or by a later fragment change — waits for the config like a click
  // would; `start`'s in-flight guard keeps a second marker from starting a second handoff.
  const ready = resolveConfig(bootstrap, true);
  configReady = ready;
  const arrive = (link: string | null): void => {
    const resume = (): void => resumeFromMarker(bootstrap, link);
    void ready.then(resume, resume);
  };
  if (marker !== null) arrive(marker.link);
  // …or the marker arriving on this very document: a browser that ignores the modifier follows the
  // swapped `href` in the same tab, which is only a fragment change — no reload, so the load path
  // above never runs. `takeMarker` strips it first and `replaceState` fires no `hashchange`, so
  // this cannot loop.
  window.addEventListener('hashchange', () => {
    const later = takeMarker();
    if (later !== null) arrive(later.link);
  });
  replayEarlyTap(early, bootstrap);
}

// Document-level delegation needs no DOM to be ready, and the style injection guards against a
// missing `<head>`, so there is nothing to wait for — `defer` or not, before or after
// `DOMContentLoaded`.
init();
